import React from 'react';
import { AuthUser, Plot, PlotRef, PlotSizeGroup } from '../types';
import { Icon } from './ui/Icon';
import { Empty, Loading, Note, Section } from './ui/primitives';
import { isMergedPlot, plotFootprint, plotRefOf, sizeLabel } from '../utils/plots';

interface Props {
  me: AuthUser;
  /** null until the first load has started. */
  sizes: PlotSizeGroup[] | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  onSelect: (ref: PlotRef) => void;
}

const PlotCard: React.FC<{ plot: Plot; onClick: () => void }> = ({ plot, onClick }) => {
  const footprint = plotFootprint(plot);
  return (
    <button type="button" className="wcard" onClick={onClick} title={`${plot.world} · ${plot.plotId}`}>
      <span className="nm">{plot.plotId}</span>
      <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <span className="chip">{plot.world}</span>
        {isMergedPlot(plot) && <span className="chip chip--frost">merged</span>}
      </span>
      <span className="meta">
        {footprint && (
          <span>
            <b>
              {footprint[0]} × {footprint[1]}
            </b>{' '}
            blocks
          </span>
        )}
        {isMergedPlot(plot) && <span>{plot.regions.length} regions</span>}
      </span>
    </button>
  );
};

/** "Your plots": the player's PlotSquared plots, one section per plot server (75, 251, 501, 1001, comp). */
const PlotsPage: React.FC<Props> = ({ me, sizes, loading, error, onRefresh, onSelect }) => {
  const groups = sizes || [];
  const total = groups.reduce((n, g) => n + g.plots.length, 0);
  const reached = groups.filter((g) => !g.error).length;
  const sub = (() => {
    if (!me.minecraftUuid) return 'Link your Minecraft account to see your plots.';
    if (sizes == null || (loading && groups.length === 0)) return 'Asking the plot servers…';
    if (groups.length === 0) return 'No plot servers are configured.';
    return `You own ${total} plot${total === 1 ? '' : 's'} across ${reached} of ${groups.length} plot server${groups.length === 1 ? '' : 's'}.`;
  })();

  return (
    <div className="main2">
      <div className="adm-head">
        <div>
          <h1>Your plots</h1>
          <div className="sub">{sub}</div>
        </div>
        <div className="adm-tools">
          <button type="button" className="btn btn--sm" onClick={onRefresh} disabled={loading}>
            {loading ? <span className="spin" /> : <Icon name="refresh" size={12} />} Refresh
          </button>
        </div>
      </div>

      <div className="page" style={{ paddingTop: 22 }}>
        {!me.minecraftUuid && (
          <Note tone="warn">No Minecraft account is linked to your Discord. Link it with DiscordSRV in-game to see your plots.</Note>
        )}
        {error && me.minecraftUuid && <Note tone="danger">{error}</Note>}

        {sizes == null || (loading && groups.length === 0) ? (
          <Loading label="Asking the plot servers…" />
        ) : groups.length === 0 ? (
          !error && (
            <Empty icon="block" title="No plot servers configured">
              Ask a Bakery dev to add the plot servers to the control service config.
            </Empty>
          )
        ) : (
          groups.map((g) => (
            <Section
              key={g.size}
              title={sizeLabel(g.size)}
              blurb={
                g.error
                  ? `The ${g.size} plot server couldn't be queried.`
                  : `${g.plots.length} plot${g.plots.length === 1 ? '' : 's'} on the ${g.size} server.`
              }
            >
              {g.error ? (
                <Note tone="danger">{g.error}</Note>
              ) : g.plots.length === 0 ? (
                <Empty icon="block" title={`No plots on ${sizeLabel(g.size)}`}>
                  You don't own a plot on the {g.size} server yet. Claim one in-game and it shows up here.
                </Empty>
              ) : (
                <div className="wgrid">
                  {g.plots.map((p) => (
                    <PlotCard key={`${p.world}/${p.plot}`} plot={p} onClick={() => onSelect(plotRefOf(g.size, p))} />
                  ))}
                </div>
              )}
            </Section>
          ))
        )}
      </div>
    </div>
  );
};

export default PlotsPage;
